import { Injectable } from 'angular-ts-decorators';

import type { IConfig } from '@shared/app/scripts/services/config';
import type { IStandardErrorParser } from '@shared/app/scripts/services/standard-error-parser';
import { Domain } from '@shared/app/scripts/services/domain';
import { SaleModel } from '@shared/app/scripts/classes/sale-model';
import { Sentry } from '@shared/app/scripts/services/sentry';
import { SpynlBase } from '@shared/app/scripts/services/spynl-base';

/**
 * Simple service to take care of communicating with new /sales/add endpoint
 */
@Injectable('sales')
export class Sales extends SpynlBase<SaleModel> {
  constructor(
    protected $http: ng.IHttpService,
    protected $q: ng.IQService,
    protected sentry: Sentry,
    protected domain: Domain,
    protected config: IConfig,
    protected standardErrorParser: IStandardErrorParser,
  ) {
    'ngInject';
    super($http, $q, sentry, domain, config, standardErrorParser, SaleModel);
    this.location = '/sales';
  }

  /**
   * Gets transaction number from /pos/init
   * @returns {promise}
   * resolve with response on success, otherwise reject with the error
   */
  posInit(): ng.IPromise<{
    counters: { consignments: number; sales: number; transits: number };
  }> {
    return this.$http
      .post(
        this.domain.getSpynl() + '/pos/init',
        {},
        {
          // done on config level for v7; setting for v6
          withCredentials: true,
        },
      )
      .then((response: any) => response.data.data);
  }

  /**
   * @param {string} _id
   * transaction id
   * @param {string} email
   * email we are sending to
   * @param {string} footer
   * Optional. Footer value read from settings
   * @param {boolean} printLoyaltyPoints
   * Optional, default to true on the backend.
   * @param {boolean} printExtendedReceipt
   * Optional. If true, print extra information for barcode items.
   * Send transation to an email, by exposing {@link
   * https://docs.dev.swcloud.nl/dev/index.html#/services/post_sales_email
   * spynl/sales/email} to frontend.
   * @returns {promise}
   * resolve with response on success, otherwise reject with the error
   */
  email(
    _id: string,
    email: string,
    footer?: string,
    printLoyaltyPoints?: boolean,
    printExtendedReceipt?: boolean,
  ): ng.IPromise<{}> {
    return this.$http
      .post(
        this.domain.getSpynl() + '/sales/email',
        {
          _id,
          email,
          footer,
          printLoyaltyPoints,
          printExtendedReceipt,
        },
        {
          // done on config level for v7; setting for v6
          withCredentials: true,
        },
      )
      .then((response: any) => response.data.data);
  }

  /**
   * @param _id
   * transaction id
   * @param fiscalReceiptNumber
   * fiscalReceiptNumber we want to add to the transaction
   * @param fiscalShiftNumber
   * fiscalShiftNumber we want to add to the transaction
   * @param fiscalDate
   * Date returned by printer
   * @param fiscalPrinterId
   * Fiscal printer id, as stored in the setting
   * @returns
   * resolve with response on success, otherwise reject with the error
   */
  addFiscalReceipt(
    _id: string,
    fiscalReceiptNumber: string,
    fiscalShiftNumber: string,
    fiscalDate: string,
    fiscalPrinterId: string,
  ): ng.IPromise<{}> {
    return this.$http
      .post(
        this.domain.getSpynl() + '/sales/add-fiscal-receipt',
        {
          _id: _id,
          fiscal_receipt_nr: fiscalReceiptNumber,
          fiscal_shift_nr: fiscalShiftNumber,
          fiscal_date: fiscalDate,
          fiscal_printer_id: fiscalPrinterId,
        },
        {
          // done on config level for v7; setting for v6
          withCredentials: true,
        },
      )
      .then((response: any) => response.data.data);
  }

  /**
   * @returns {promise}
   * Resolve with response on success, otherwise reject with the error
   */
  startNew(): ng.IPromise<{}> {
    return this.$http
      .post(
        this.domain.getSpynl() + '/sales/start-new',
        {},
        {
          // done on config level for v7; setting for v6
          withCredentials: true,
        },
      )
      .then((response: any) => response.data.data);
  }
}
